import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";
import Button from "components/buttons/Button";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import { Modal } from "components/Modal/Modal";
import ModalSkeleton from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import { handleApiError } from "helpers/handleApiError";
import { useFormErrors } from "helpers/useFormErrors";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { closeModal } from "store/modals/slice";
import { useAppDispatch, useAppSelector } from "store/index";
import {
  createDepositPointLimit,
  deleteDepositPointLimit,
  getDepositPointLimit,
  handleClearFields,
  handleUpdateFields,
  updateDepositPointLimit,
} from "store/liveOps/loyalty/depositPointLimits/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import styles from "./LiveOpsLoyaltyPointsEditModal.module.scss";

const PERIOD_LABELS: Record<string, string> = {
  DAY: "day",
  WEEK: "week",
  MONTH: "month",
};

const LiveOpsDepositPointLimitModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const formErrors = useFormErrors();
  const entityId = searchParams.get("entityId");
  const isEdit = Boolean(entityId);
  const permissions = usePermissionHelper();
  const canCreate = permissions?.create ?? false;
  const canUpdate = permissions?.update ?? false;
  const canDelete = permissions?.delete ?? false;
  const canEdit = isEdit ? canUpdate : canCreate;

  const isItemFetched = useAppSelector(
    (store) => store["deposit-point-limits"].isItemFetched,
  );
  const isItemFetching = useAppSelector(
    (store) => store["deposit-point-limits"].isItemFetching,
  );
  const payload = useAppSelector((store) => store["deposit-point-limits"].payload);
  const period = useAppSelector((store) => store["deposit-point-limits"].period);

  const [loyaltyLevelOptions, setLoyaltyLevelOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [loyaltyLevelsLoading, setLoyaltyLevelsLoading] = useState(false);
  const [loyaltyLevelsError, setLoyaltyLevelsError] = useState(false);

  useEffect(() => {
    if (entityId && !isItemFetched) {
      dispatch(getDepositPointLimit(entityId));
    }
  }, [dispatch, entityId, isItemFetched]);

  useEffect(() => {
    let cancelled = false;
    const loadLevels = async () => {
      setLoyaltyLevelsLoading(true);
      setLoyaltyLevelsError(false);
      try {
        const limit = 1000;
        let offset = 0;
        let totalPages = 1;
        const levels: { id?: number; levelNumber?: string | number; title?: string }[] = [];
        while (offset < totalPages) {
          const result = await instance.post(apiRoutes.loyaltyLevels, {
            limit,
            offset,
            isActive: -1,
            order: { levelNumber: "asc" },
          });
          if (cancelled) return;
          if (Array.isArray(result.data?.items)) levels.push(...result.data.items);
          totalPages = Number(result.data?.totalPages) || 1;
          offset += 1;
        }
        if (cancelled) return;
        setLoyaltyLevelOptions(
          levels
            .filter((level) => level.id != null)
            .map((level) => ({
              value: Number(level.id),
              label: `${level.levelNumber ?? ""} ${level.title ?? ""}`.trim(),
            })),
        );
      } catch {
        if (!cancelled) setLoyaltyLevelsError(true);
      } finally {
        if (!cancelled) setLoyaltyLevelsLoading(false);
      }
    };
    loadLevels();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCloseClick = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleClearFields());
  };

  const handleApply = async () => {
    if (!canEdit) return;
    formErrors.resetErrors();
    const loyaltyLevelId = Number(payload?.loyaltyLevelId);
    const pointsLimit = String(payload?.pointsLimit ?? "").trim();
    if (!Number.isInteger(loyaltyLevelId) || loyaltyLevelId <= 0) {
      formErrors.setFieldError("loyaltyLevelId", "InvalidFormat");
      return;
    }
    if (!/^(?:0|[1-9]\d*)(?:\.\d{1,8})?$/.test(pointsLimit)) {
      formErrors.setFieldError("pointsLimit", "InvalidFormat");
      return;
    }
    try {
      if (isEdit) {
        await dispatch(
          updateDepositPointLimit({
            id: Number(entityId),
            loyaltyLevelId,
            pointsLimit,
          }),
        ).unwrap();
      } else {
        await dispatch(createDepositPointLimit({ loyaltyLevelId, pointsLimit })).unwrap();
      }
      dispatch(
        showNotification({
          title: t("success"),
          message: isEdit ? t("entityUpdateSuccess") : t("entityCreateSuccess"),
          type: NotificationTypes.success,
        }),
      );
      handleCloseClick();
    } catch (error) {
      handleApiError({ error, t, dispatch, formErrors });
    }
  };

  const handleDelete = async () => {
    if (!canDelete || !entityId) return;
    const result = await dispatch(deleteDepositPointLimit(Number(entityId)));
    if (result.payload === true) {
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityDeleteSuccess"),
          type: NotificationTypes.success,
        }),
      );
      handleCloseClick();
      return;
    }
    dispatch(
      showNotification({
        title: t("error"),
        message: t("entityDeleteError"),
        type: NotificationTypes.error,
      }),
    );
  };

  const loaded = !entityId || isItemFetched;
  const periodKey = PERIOD_LABELS[period] || "day";

  return (
    <Modal onClose={handleCloseClick} size="sm">
      {!loaded ? (
        <ModalSkeleton />
      ) : (
        <>
          <Modal.Header>{t("depositPointsLimit")}</Modal.Header>
          <Modal.Content className={styles.liveopsLoyaltyPointsEditModalContentWrapper}>
            <div className={`loyalty-points-content ${styles.depositContent}`}>
              <div className="loyalty-points-form-box--item">
                <DefaultDropdown
                  disabled={!canEdit || loyaltyLevelsLoading || loyaltyLevelsError}
                  loading={loyaltyLevelsLoading}
                  value={payload?.loyaltyLevelId ?? ""}
                  label={t("loyaltyLevel")}
                  placeholder={t("loyaltyLevel")}
                  options={loyaltyLevelOptions}
                  onSelect={(value) => {
                    formErrors.clearFieldError("loyaltyLevelId");
                    dispatch(handleUpdateFields({ loyaltyLevelId: Number(value) }));
                  }}
                  hasError={!!formErrors.errors.loyaltyLevelId}
                />
              </div>
              <div className="loyalty-points-form-box--item">
                <InnerInput
                  disabled={!canEdit}
                  value={payload?.pointsLimit ?? ""}
                  label={t("depositPointsLimit")}
                  placeholder={t("depositPointsLimit")}
                  type="number"
                  inputMode="decimal"
                  min={0}
                  id="deposit-points-limit"
                  onChange={(value) => {
                    formErrors.clearFieldError("pointsLimit");
                    dispatch(handleUpdateFields({ pointsLimit: value }));
                  }}
                  hasError={!!formErrors.errors.pointsLimit}
                />
              </div>
              <div className="loyalty-points-form-box--item">
                <InnerInput
                  disabled
                  value={`${t(periodKey)} (UTC)`}
                  label={t("globalPeriod")}
                  tooltipText={t("depositPointsLimitPeriodHint")}
                  type="text"
                  id="deposit-points-limit-period"
                  onChange={() => undefined}
                />
              </div>
            </div>
          </Modal.Content>
          <Modal.Footer>
            {isEdit && canDelete && (
              <Button
                isDelete
                className={styles.deleteButton}
                txt={t("delete")}
                handleClick={handleDelete}
              />
            )}
            <Button isSecondary txt={t("cancel")} handleClick={handleCloseClick} />
            {canEdit && (
              <Button
                isPrimary
                txt={t("save")}
                handleClick={handleApply}
                isLoad={isItemFetching}
                disabled={loyaltyLevelsLoading}
              />
            )}
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
};

export default LiveOpsDepositPointLimitModal;
